import React, { useState, useEffect } from 'react';
import './usertools-css/VitaminDCalculator.css';
import '../../styles/components/ainutritionlandingpage.css';


// 2. Reference Intakes (RDA / AI) — Institute of Medicine
const vitDRdaTable = { // IU / day
    '0-12m': { rda: 400, isAI: true }, // AI
    '1-70y': { rda: 600 },
    '71+y': { rda: 800 },
    'pregnant_lactating': { rda: 600 },
};

// 5. Data tables to preload (simplified)
const vitDUlTable = { // IU / day
    '0-6m': 1000,
    '7-12m': 1500, // ODS has 1500 for 7-12mo, then 2500 1-3y, 3000 4-8y
    '1-3y': 2500,
    '4-8y': 3000,
    '9+y': 4000,
    'pregnant_lactating': 4000,
};

// 5. Food vitamin D values (IU per serving)
const foodVitDValues = {
    salmon_sockeye_baked_75g: 570,
    canned_tuna_75g: 230,
    egg_large: 45,
    fortified_milk_cup: 120,
    fortified_oj_cup: 100,
    uv_mushrooms_half_cup: 375,
};

// 5. BMI multipliers
const bmiMultipliers = {
    normal: 1.0,
    overweight: 1.5,
    obese_low: 2.0, // Providing options based on 2-3x range
    obese_high: 3.0,
};

// 5. Medication penalties (simplified as fraction remaining)
const medicationAbsorptionFactors = {
    glucocorticoids: 0.8, // Example: reduces effective D by 20%
    anticonvulsants: 0.8, // Example
    orlistat: 0.7,        // Example (affects fat absorption)
    rifampin: 0.8,        // Example
    // Note: PPIs are indirect (Mg), not included as direct penalty here
};

// --- Helper Functions ---

// Simplistic function to get RDA key based on age/status
const getRdaVitDKey = (ageNum, isPregnant, isLactating) => {
    if (isPregnant || isLactating) return 'pregnant_lactating';
    if (ageNum < 1) return '0-12m';
    if (ageNum >= 1 && ageNum <= 70) return '1-70y';
    if (ageNum > 70) return '71+y';
    return '1-70y'; // Default fallback
};

// Simplistic function to get UL key based on age/status
const getUlVitDKey = (ageNum, isPregnant, isLactating) => {
    if (isPregnant || isLactating) return 'pregnant_lactating';
    if (ageNum < 0.5) return '0-6m';
    if (ageNum >= 0.5 && ageNum < 1) return '7-12m';
    if (ageNum >= 1 && ageNum <= 3) return '1-3y';
    if (ageNum >= 4 && ageNum <= 8) return '4-8y';
    if (ageNum >= 9) return '9+y';
    return '9+y'; // Default fallback
};

// VERY SIMPLIFIED Sun Synthesis Coefficient Stub (IU per min)
// A real implementation needs a complex lookup based on UV index, lat, season, time, skin area etc.
const getSunSynthesisCoefficient = (latitude, month, skinTone, clothingCoveragePercent, sunscreenSpf) => {
    let baseCoeff = 0;
    const absLat = Math.abs(latitude);
    const isWinter = month >= 11 || month <= 2; // Rough Northern Hemisphere winter
    const isSummer = month >= 5 && month <= 8; // Rough Northern Hemisphere summer

    // Very rough base potential based on latitude and season
    if (absLat < 37 && isSummer) baseCoeff = 100; // High potential zone, summer
    else if (absLat < 37 && !isWinter) baseCoeff = 50; // High potential zone, shoulder season
    else if (absLat >= 37 && isSummer) baseCoeff = 40; // Lower potential zone, summer
    else baseCoeff = 5; // Low potential (high lat winter, etc.)

    // Adjust for skin tone
    if (skinTone === 'medium') baseCoeff *= 0.6;
    else if (skinTone === 'dark') baseCoeff *= 0.3;

    // Adjust for clothing and sunscreen
    const skinExposedFactor = Math.max(0, (100 - clothingCoveragePercent) / 100);
    let spfFactor = 1.0;
    if (sunscreenSpf >= 15) spfFactor = 0.1; // SPF 15 blocks ~93%, SPF 30 ~97% - simplify drastically
    else if (sunscreenSpf > 0) spfFactor = 0.5;

    return baseCoeff * skinExposedFactor * spfFactor;
};


// --- Component ---
function VitaminDCalculator() { // <<< RENAMED Component
    // --- State for User Inputs ---
    const [age, setAge] = useState('');
    const [sex, setSex] = useState(''); // 'male', 'female' - needed for context/UI disabling
    const [isPregnant, setIsPregnant] = useState(false);
    const [isLactating, setIsLactating] = useState(false);

    // Location & Season
    const [latitude, setLatitude] = useState(''); // Degrees N/S
    const [currentMonth] = useState(new Date().getMonth() + 1); // Auto-detect month (1-12)

    // Skin & Sun
    const [skinTone, setSkinTone] = useState(''); // 'fair', 'medium', 'dark'
    const [sunMinutes, setSunMinutes] = useState(''); // Daily unprotected minutes
    const [clothingCoveragePercent, setClothingCoveragePercent] = useState(''); // % covered
    const [sunscreenSpf, setSunscreenSpf] = useState(''); // SPF value (0 if none)

    // Lifestyle Boosts (Simplified)
    const [lifestyleFactors, setLifestyleFactors] = useState({
        outdoorJob: false,
        sunnyVacation: false, // Recent/frequent
        tanningBedUse: false,
    });

    // Diet (Weekly Servings)
    const [dietServings, setDietServings] = useState({
        fattyFish: '', // Includes salmon, tuna
        eggs: '',
        mushroomsUV: '', // Specifically UV-exposed
        fortifiedMilk: '',
        fortifiedOJ: '',
        fortifiedCereal: '', // Generic placeholder for fortified foods
    });

    // Supplements
    const [takesSupplement, setTakesSupplement] = useState(false);
    const [supplementDoseIU, setSupplementDoseIU] = useState('');
    const [supplementFrequency, setSupplementFrequency] = useState(''); // 'daily', 'weekly', 'other'

    // BMI
    const [bmiCategory, setBmiCategory] = useState(''); // 'normal', 'overweight', 'obese_low', 'obese_high'

    // Medications
    const [medications, setMedications] = useState({
        glucocorticoids: false,
        anticonvulsants: false,
        orlistat: false,
        rifampin: false,
    });

    // Health Flags
    const [healthFlags, setHealthFlags] = useState({
        giDisease: false,       // IBD, celiac, bariatric
        liverDisease: false,    // Impaired 25-hydroxylation
        kidneyDisease: false,   // Impaired 1-hydroxylation
        hyperparathyroidism: false, // Affects calcium/VitD - caution
    });

    // Optional Known Serum Level
    const [knownSerumLevelNgMl, setKnownSerumLevelNgMl] = useState('');

    // --- State for Results ---
    const [calculationResult, setCalculationResult] = useState(null);
    const [showResults, setShowResults] = useState(false);

    // --- Input Handlers ---
    const handleLifestyleFactorChange = (event) => {
        const { name, checked } = event.target;
        setLifestyleFactors(prev => ({ ...prev, [name]: checked }));
    };
    const handleDietChange = (event) => {
        const { name, value } = event.target;
        setDietServings(prev => ({ ...prev, [name]: value }));
    };
    const handleMedicationChange = (event) => {
        const { name, checked } = event.target;
        setMedications(prev => ({ ...prev, [name]: checked }));
    };
    const handleHealthFlagChange = (event) => {
        const { name, checked } = event.target;
        setHealthFlags(prev => ({ ...prev, [name]: checked }));
    };

    // --- Derived State ---
    const ageNum = parseInt(age, 10);
    const isFemale = sex === 'female';

    // Disable pregnancy/lactation based on sex and age (rough)
    useEffect(() => {
        if (sex === 'male' || ageNum < 12 || ageNum > 55) {
            setIsPregnant(false);
            setIsLactating(false);
        }
    }, [sex, ageNum]);

    // --- Calculation Logic ---
    const calculateVitaminDIntake = (e) => {
        e.preventDefault();
        setShowResults(false);
        setCalculationResult(null);

        let explanation = [];
        let safetyFlags = [];
        let suggestions = { sun: [], food: [], supplement: [], test: [] };
        let targetRda = 0;

        // Validate essential inputs
        if (isNaN(ageNum) || !sex || !latitude || !skinTone || !bmiCategory) {
            alert("Please fill in all required fields: Age, Sex, Latitude, Skin Tone, and Weight Class.");
            return;
        }
        const latNum = parseFloat(latitude);
        if (isNaN(latNum) || latNum < -90 || latNum > 90) {
            alert("Please enter a valid latitude (-90 to 90).");
            return;
        }

        // Calculate absLat and isWinter
        const absLat = Math.abs(latNum);
        const isWinter = currentMonth >= 11 || currentMonth <= 2;

        // 1. Base target (RDA & BMI Multiplier)
        const rdaKey = getRdaVitDKey(ageNum, isPregnant, isLactating);
        if (vitDRdaTable[rdaKey]) {
            targetRda = vitDRdaTable[rdaKey].rda;
            explanation.push(`Base ${vitDRdaTable[rdaKey].isAI ? 'AI' : 'RDA'} (${rdaKey.replace('_', ' ')}): ${targetRda} IU/day.`);

            const multiplier = bmiMultipliers[bmiCategory] || 1.0;
            if (multiplier > 1.0) {
                targetRda *= multiplier;
                explanation.push(`x${multiplier} multiplier applied for ${bmiCategory.replace('_', ' ')} weight status.`);
            }
            explanation.push(`-> Adjusted Target Intake: ${targetRda.toFixed(0)} IU/day.`);
        } else {
            explanation.push("Could not determine base RDA. Using default.");
            targetRda = vitDRdaTable['1-70y'].rda; // Fallback
        }

        // 2. Estimate cutaneous synthesis
        const sunMins = parseInt(sunMinutes, 10) || 0;
        const coverage = parseInt(clothingCoveragePercent, 10) || 0;
        const spf = parseInt(sunscreenSpf, 10) || 0;
        const coeff = getSunSynthesisCoefficient(latNum, currentMonth, skinTone, coverage, spf);
        let sun_IU = sunMins * coeff;

        // Add lifestyle boosts (very simplified)
        let lifestyleBoostFactor = 1.0;
        if (lifestyleFactors.outdoorJob) lifestyleBoostFactor += 0.3; // 30% boost example
        if (lifestyleFactors.sunnyVacation) lifestyleBoostFactor += 0.2; // 20% boost example
        if (lifestyleFactors.tanningBedUse) sun_IU += 500; // Arbitrary fixed boost for tanning

        sun_IU *= lifestyleBoostFactor;

        const maxSkinSynthesis = 10000; // Cap daily synthesis
        sun_IU = Math.min(sun_IU, maxSkinSynthesis);
        explanation.push(`Estimated skin synthesis: ${sun_IU.toFixed(0)} IU/day (based on ${sunMins} min exposure, lat ${latitude}°, month ${currentMonth}, ${skinTone} skin, ${coverage}% cover, SPF ${spf}, lifestyle factors). Max potential capped at ${maxSkinSynthesis} IU.`);
        if (coeff === 0 && sunMins > 0) {
            explanation.push(`(Note: Calculated synthesis coefficient is near zero, likely due to high latitude/winter/coverage/SPF).`);
        } else if (sunMins > 0) {
            suggestions.sun.push(`Your current sun exposure provides roughly ${sun_IU.toFixed(0)} IU/day. To get more, consider safe, short (10-20 min) midday exposure on more skin area when UV index is moderate, avoiding burns.`);
            // Example specific tip:
            const tipCoeff = getSunSynthesisCoefficient(latNum, currentMonth, skinTone, 25, 0); // Arms/face exposed, no SPF
            if (tipCoeff > 0) {
                const timeFor600IU = 600 / tipCoeff;
                suggestions.sun.push(`Tip: ~${Math.ceil(timeFor600IU)} min midday sun on arms/face this month could provide ~600 IU.`);
            }
        }
        if (absLat >= 37 && isWinter) {
            suggestions.sun.push(`At your latitude (${latitude}°) in winter, sun synthesis is likely minimal. Rely more on diet/supplements.`);
        }


        // 3. Dietary + fortified intake (Average Daily IU)
        let diet_IU = 0;
        const servings = {
            fattyFish: parseInt(dietServings.fattyFish, 10) || 0,
            eggs: parseInt(dietServings.eggs, 10) || 0,
            mushroomsUV: parseInt(dietServings.mushroomsUV, 10) || 0,
            fortifiedMilk: parseInt(dietServings.fortifiedMilk, 10) || 0,
            fortifiedOJ: parseInt(dietServings.fortifiedOJ, 10) || 0,
            fortifiedCereal: parseInt(dietServings.fortifiedCereal, 10) || 0, // Assumed ~100 IU per serving
        };
        diet_IU += (servings.fattyFish * (foodVitDValues.salmon_sockeye_baked_75g + foodVitDValues.canned_tuna_75g) / 2) / 7; // Avg fatty fish
        diet_IU += (servings.eggs * foodVitDValues.egg_large) / 7;
        diet_IU += (servings.mushroomsUV * foodVitDValues.uv_mushrooms_half_cup) / 7;
        diet_IU += (servings.fortifiedMilk * foodVitDValues.fortified_milk_cup) / 7;
        diet_IU += (servings.fortifiedOJ * foodVitDValues.fortified_oj_cup) / 7;
        diet_IU += (servings.fortifiedCereal * 100) / 7; // Placeholder for fortified cereal

        explanation.push(`Estimated average dietary intake: ${diet_IU.toFixed(0)} IU/day (from weekly servings).`);

        // Food Suggestions
        const topFoods = Object.entries(foodVitDValues)
            .sort(([, valA], [, valB]) => valB - valA)
            .slice(0, 3);
        suggestions.food.push(`Top food sources per serving: ${topFoods.map(([key, val]) => `${key.replace(/_/g, ' ')} (${val} IU)`).join(', ')}.`);
        if (diet_IU < 200) {
            suggestions.food.push("Consider adding more fatty fish (salmon, tuna), fortified milk/OJ, or UV-exposed mushrooms.");
        }


        // 4. Supplemental intake
        let supp_IU = 0;
        const dose = parseInt(supplementDoseIU, 10);
        if (takesSupplement && !isNaN(dose) && dose > 0 && supplementFrequency) {
            let dailyFactor = 0;
            switch (supplementFrequency) {
                case 'daily': dailyFactor = 1; break;
                case 'alternate_day': dailyFactor = 0.5; break;
                case '3x_week': dailyFactor = 3 / 7; break;
                case 'weekly': dailyFactor = 1 / 7; break;
                case 'monthly': dailyFactor = 1 / 30; break; // Added monthly
                default: dailyFactor = 0; break;
            }
            supp_IU = dose * dailyFactor;
            explanation.push(`Supplement contribution: ${dose} IU taken ${supplementFrequency.replace('_', ' ')} ≈ ${supp_IU.toFixed(0)} IU/day average.`);
        }

        // 5. Adjust for meds / malabsorption
        let abs_factor = 1.0;
        let penaltiesApplied = [];
        if (healthFlags.giDisease || healthFlags.liverDisease || healthFlags.kidneyDisease) {
            abs_factor *= 0.7; // Simplified penalty for any major absorption/metabolism issue
            penaltiesApplied.push('Health condition affecting absorption/metabolism');
            safetyFlags.push({ type: 'info', text: 'Condition affecting Vitamin D metabolism/absorption selected. Needs may be higher and require medical management.' });
        }
        Object.entries(medications).forEach(([med, isTaken]) => {
            if (isTaken && medicationAbsorptionFactors[med]) {
                abs_factor *= medicationAbsorptionFactors[med];
                penaltiesApplied.push(`Medication (${med})`);
            }
        });

        if (penaltiesApplied.length > 0) {
            explanation.push(`Absorption/Metabolism factor applied: ${abs_factor.toFixed(2)}x due to (${penaltiesApplied.join(', ')}).`);
        }

        // Apply factor to sun & diet, NOT supplements (as per pseudo-code)
        const effective_IU = (sun_IU + diet_IU) * abs_factor + supp_IU;
        explanation.push(`==> Estimated TOTAL EFFECTIVE intake (Sun*Factor + Diet*Factor + Supplement): ${effective_IU.toFixed(0)} IU/day.`);


        // 6. Gap & safety
        const gap = targetRda - effective_IU;
        explanation.push(`Gap compared to Target: ${gap > 0 ? `${gap.toFixed(0)} IU/day deficit` : `${Math.abs(gap).toFixed(0)} IU/day surplus (or adequate)`}.`);

        const ulKey = getUlVitDKey(ageNum, isPregnant, isLactating);
        const currentUL = vitDUlTable[ulKey] || 4000; // Default UL
        explanation.push(`Tolerable Upper Intake Level (UL) for age ${ageNum}: ${currentUL} IU/day.`);

        // Safety Flags
        const totalIntakeEstimateForUL = sun_IU + diet_IU + supp_IU; // Simple sum for UL check context
        if (supp_IU > currentUL) {
            safetyFlags.push({ type: 'severe', text: `Your average DAILY SUPPLEMENTAL intake alone (${supp_IU.toFixed(0)} IU) exceeds the Tolerable Upper Intake Level (UL) of ${currentUL} IU/day. Chronic excess can cause hypercalcemia. CONSULT YOUR DOCTOR.` });
        } else if (totalIntakeEstimateForUL > currentUL && supp_IU > 0) {
            safetyFlags.push({ type: 'warning', text: `Your estimated TOTAL daily intake (~${totalIntakeEstimateForUL.toFixed(0)} IU from sun, diet, supplements) may exceed the UL of ${currentUL} IU/day. Discuss your total intake with your doctor, especially long-term.` });
        }

        if ((healthFlags.kidneyDisease || healthFlags.hyperparathyroidism) && supp_IU > 2000) {
            safetyFlags.push({ type: 'severe', text: `High supplemental intake (${supp_IU.toFixed(0)} IU/day) with Kidney Disease or Hyperparathyroidism requires close MEDICAL SUPERVISION due to risks of hypercalcemia. Consult your doctor.` });
        } else if ((healthFlags.kidneyDisease || healthFlags.hyperparathyroidism) && supp_IU > 0) {
            safetyFlags.push({ type: 'warning', text: `Taking supplements with Kidney Disease or Hyperparathyroidism requires caution. Ensure dosage is approved by your doctor.` });
        }

        // Suggestions based on Gap
        if (gap > 0) {
            suggestions.supplement.push(`You are estimated to be ${gap.toFixed(0)} IU short of your daily target.`);
            if (gap >= 800 && !takesSupplement) {
                suggestions.supplement.push("Consider a Vitamin D3 supplement of 1000-2000 IU daily, especially if sun exposure and dietary intake are consistently low. Discuss with your doctor first.");
                // Placeholder for affiliate link/product suggestion:
                // suggestions.supplement.push("Example: [Link to 1000 IU D3 Softgel]");
            } else if (takesSupplement && gap > 400) {
                suggestions.supplement.push("Your current intake may still be insufficient. Review your sun, diet, and supplement strategies. Consider discussing a dose adjustment or testing with your doctor.");
            } else {
                suggestions.supplement.push("Focus on increasing sun exposure (safely) and dietary sources first.");
            }
        } else {
            suggestions.supplement.push("Your estimated intake meets or exceeds your target. Ensure your total intake, especially from supplements, stays below the UL.");
        }

        // Serum Level Context
        const serumLevel = parseFloat(knownSerumLevelNgMl);
        if (!isNaN(serumLevel)) {
            explanation.push(`Your reported 25-OH-D level: ${serumLevel.toFixed(1)} ng/mL.`);
            let interpretation = '';
            if (serumLevel < 12) interpretation = 'Deficient';
            else if (serumLevel < 20) interpretation = 'Insufficient';
            else if (serumLevel < 30) interpretation = 'Suboptimal (often considered insufficient)';
            else if (serumLevel >= 30 && serumLevel <= 50) interpretation = 'Generally considered adequate/optimal'; // ODS range is wide, clinical goals vary
            else if (serumLevel > 50 && serumLevel <= 100) interpretation = 'Adequate, potentially high normal';
            else interpretation = 'Very high (potential risk > 100 ng/mL)';
            explanation.push(`Interpretation based on common ranges: ${interpretation}. (1 ng/mL ≈ 2.5 nmol/L)`);
            safetyFlags.push({ type: 'info', text: `Your known level (${serumLevel.toFixed(1)} ng/mL) suggests ${interpretation}. Use this calculator as a guide, but follow your doctor's advice based on your test results.` });
        } else if (effective_IU < (vitDRdaTable[rdaKey]?.rda || 600)) { // If level unknown and intake < RDA
            suggestions.test.push("Your estimated intake may be below the recommended level, and your serum Vitamin D level is unknown. Consider discussing a 25-OH-D blood test with your doctor to assess your status accurately.");
        }


        // Set results after a short delay
        setTimeout(() => {
            setCalculationResult({
                targetRda: targetRda.toFixed(0),
                sunIU: sun_IU.toFixed(0),
                dietIU: diet_IU.toFixed(0),
                suppIU: supp_IU.toFixed(0),
                absorptionFactor: abs_factor.toFixed(2),
                effectiveTotalIntake: effective_IU.toFixed(0),
                gap: gap.toFixed(0),
                ul: currentUL,
                explanation: explanation.join('\n'),
                suggestions: suggestions, // Contains sun, food, supplement, test arrays
                safetyFlags: safetyFlags,
                serumLevelProvided: !isNaN(serumLevel) ? serumLevel.toFixed(1) : null,
            });
            setShowResults(true);
        }, 300);
    };


    // --- JSX Structure ---
    return (
        <div className="vitaminDCalculator"> {/* <<< UPDATED CLASS */}
            <h1>
                <span>
                    <span role="img" aria-label="vitamin D calculator">
                        ☀️
                    </span>{' '}
                    Vitamin D Calculator
                </span>

                <span className="ai-promo-inline">
                    <strong>Unlock Smarter Eating:</strong>{' '}
                    Get a complete breakdown of vitamins, minerals, and fats plus a personalized
                    weight-loss plan or nutrition tailored to your training with a single{' '}
                    <a
                        href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
                        className="ai-promo-link"
                    >
                        AI scan
                    </a>.
                </span>
            </h1>
            <p className="important-note">
                <strong>Disclaimer:</strong> This calculator provides an ESTIMATE of your Vitamin D intake based on general data and your inputs. It is NOT a substitute for professional medical advice, diagnosis, or treatment. Vitamin D synthesis, absorption, and needs vary greatly. Consult your healthcare provider regarding your Vitamin D status, especially if you have health conditions, take medications, are pregnant/lactating, have symptoms of deficiency, or are considering supplementation. A 25-hydroxyvitamin D blood test is the only way to accurately determine your status. Do not exceed the Tolerable Upper Intake Level (UL) without medical supervision.
            </p>

            {/* --- Input Form --- */}
            <form onSubmit={calculateVitaminDIntake}> {/* <<< UPDATED HANDLER */}
                <h2>🧾 Your Information</h2>

                {/* Demographics */}
                <div className="form-section">
                    <h3>👤 Demographics</h3>
                    <label>
                        Age (Years):*
                        <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="0" max="120" placeholder="e.g., 35" />
                    </label>
                    <label>
                        Sex Assigned at Birth:*
                        <select value={sex} onChange={(e) => setSex(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                        </select>
                    </label>
                    {isFemale && (
                        <>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isPregnant} onChange={(e) => setIsPregnant(e.target.checked)} disabled={isLactating || !isFemale || ageNum < 12 || ageNum > 55} />
                                Currently Pregnant?
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isLactating} onChange={(e) => setIsLactating(e.target.checked)} disabled={isPregnant || !isFemale || ageNum < 12 || ageNum > 55} />
                                Currently Lactating?
                            </label>
                        </>
                    )}
                </div>

                {/* Location & Season */}
                <div className="form-section">
                    <h3>📍 Location & Sun Exposure Potential</h3>
                    <label>
                        Select Your Location:*
                        <select onChange={(e) => setLatitude(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="40.7">New York City, USA (40.7° N)</option>
                            <option value="-33.8">Sydney, Australia (-33.8° S)</option>
                            <option value="60.1">Stockholm, Sweden (60.1° N)</option>
                            <option value="51.5">London, UK (51.5° N)</option>
                            <option value="-34.6">Cape Town, South Africa (-34.6° S)</option>
                            <option value="37.8">San Francisco, USA (37.8° N)</option>
                            <option value="48.9">Paris, France (48.9° N)</option>
                            <option value="-23.5">São Paulo, Brazil (-23.5° S)</option>
                            {/* Nordic Countries */}
                            <option value="59.9">Oslo, Norway (59.9° N)</option>
                            <option value="60.2">Helsinki, Finland (60.2° N)</option>
                            <option value="64.1">Reykjavik, Iceland (64.1° N)</option>
                            <option value="55.7">Copenhagen, Denmark (55.7° N)</option>
                        </select>
                        <small>If your location is not listed, you can manually enter your latitude below.</small>
                    </label>
                    <label>
                        Latitude (Degrees, N+/S-):*
                        <input
                            type="number"
                            value={latitude}
                            onChange={(e) => setLatitude(e.target.value)}
                            required
                            step="any"
                            min="-90"
                            max="90"
                            placeholder="e.g., 40.7 (NYC), -33.8 (Sydney)"
                        />
                        <small>Find your latitude <a href="https://www.latlong.net/" target="_blank" rel="noopener noreferrer">here</a>.</small>
                    </label>
                    <label>
                        Current Month:
                        <input type="text" value={new Date().toLocaleString('default', { month: 'long' })} disabled readOnly />
                        <small>Used with latitude for seasonal UV estimate.</small>
                    </label>
                </div>

                {/* Skin & Sun */}
                <div className="form-section">
                    <h3>☀️ Skin Type & Sun Habits</h3>
                    <label>
                        Skin Tone (Fitzpatrick approx.):*
                        <select value={skinTone} onChange={(e) => setSkinTone(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="fair">Fair (I-II: Burns easily, tans minimally)</option>
                            <option value="medium">Medium (III-IV: Burns moderately, tans gradually)</option>
                            <option value="dark">Dark (V-VI: Rarely burns, tans easily/darkly)</option>
                        </select>
                        <small>Darker skin produces less Vitamin D for the same sun exposure.</small>
                    </label>
                    <label>
                        Avg. Daily UNPROTECTED Sun Exposure (Minutes):
                        <input type="number" value={sunMinutes} onChange={(e) => setSunMinutes(e.target.value)} min="0" placeholder="e.g., 15" />
                        <small>Time spent outside without sunscreen on significant skin area (e.g., arms, face).</small>
                    </label>
                    <label>
                        Typical Clothing Coverage (% of body):
                        <input type="number" value={clothingCoveragePercent} onChange={(e) => setClothingCoveragePercent(e.target.value)} min="0" max="100" placeholder="e.g., 20 (shorts/t-shirt), 90 (full cover)" />
                    </label>
                    <label>
                        Sunscreen SPF used (if any):
                        <input type="number" value={sunscreenSpf} onChange={(e) => setSunscreenSpf(e.target.value)} min="0" placeholder="e.g., 0, 15, 30, 50" />
                        <small>Enter 0 if no sunscreen typically used during exposure time.</small>
                    </label>
                </div>

                {/* Lifestyle Boosts */}
                <div className="form-section">
                    <h3>🤸 Lifestyle Factors</h3>
                    <fieldset>
                        <legend>Check any that apply (may increase UV exposure):</legend>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="outdoorJob" checked={lifestyleFactors.outdoorJob} onChange={handleLifestyleFactorChange} />
                            Work primarily outdoors?
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="sunnyVacation" checked={lifestyleFactors.sunnyVacation} onChange={handleLifestyleFactorChange} />
                            Recent or frequent sunny vacations / high sun activities?
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="tanningBedUse" checked={lifestyleFactors.tanningBedUse} onChange={handleLifestyleFactorChange} />
                            Use tanning beds? (Note: Health risks associated)
                        </label>
                    </fieldset>
                </div>


                {/* Diet */}
                <div className="form-section">
                    <h3>🍽️ Dietary Intake (Average Weekly Servings)</h3>
                    <small>Enter typical number of servings PER WEEK.</small>
                    <div className="diet-grid">
                        <label>
                            Fatty Fish (Salmon, Tuna, Mackerel):
                            <input type="number" name="fattyFish" value={dietServings.fattyFish} onChange={handleDietChange} min="0" placeholder="e.g., 2" />
                            <small>(~75g / 3oz per serving)</small>
                        </label>
                        <label>
                            Eggs (Large):
                            <input type="number" name="eggs" value={dietServings.eggs} onChange={handleDietChange} min="0" placeholder="e.g., 5" />
                        </label>
                        <label>
                            UV-Exposed Mushrooms:
                            <input type="number" name="mushroomsUV" value={dietServings.mushroomsUV} onChange={handleDietChange} min="0" placeholder="e.g., 1" />
                            <small>(~1/2 cup per serving; check label)</small>
                        </label>
                        <label>
                            Fortified Milk (Dairy/Plant):
                            <input type="number" name="fortifiedMilk" value={dietServings.fortifiedMilk} onChange={handleDietChange} min="0" placeholder="e.g., 7" />
                            <small>(~1 cup / 240ml per serving)</small>
                        </label>
                        <label>
                            Fortified Orange Juice:
                            <input type="number" name="fortifiedOJ" value={dietServings.fortifiedOJ} onChange={handleDietChange} min="0" placeholder="e.g., 3" />
                            <small>(~1 cup / 240ml per serving)</small>
                        </label>
                        <label>
                            Other Fortified Foods (Cereal, etc.):
                            <input type="number" name="fortifiedCereal" value={dietServings.fortifiedCereal} onChange={handleDietChange} min="0" placeholder="e.g., 4" />
                            <small>(Assume ~100 IU per serving; check labels)</small>
                        </label>
                    </div>
                </div>

                {/* Supplements */}
                <div className="form-section">
                    <h3>💊 Supplement Use</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={takesSupplement} onChange={(e) => setTakesSupplement(e.target.checked)} />
                        Do you currently take a Vitamin D supplement?
                    </label>
                    {takesSupplement && (
                        <div className='supplement-details'>
                            <label>
                                Dose per intake (IU):
                                <input type="number" value={supplementDoseIU} onChange={(e) => setSupplementDoseIU(e.target.value)} required={takesSupplement} min="1" placeholder="e.g., 1000, 5000" />
                            </label>
                            <label>
                                Frequency:
                                <select value={supplementFrequency} onChange={(e) => setSupplementFrequency(e.target.value)} required={takesSupplement}>
                                    <option value="">-- Select --</option>
                                    <option value="daily">Daily</option>
                                    <option value="alternate_day">Alternate Days</option>
                                    <option value="3x_week">3 times per week</option>
                                    <option value="weekly">Once per week</option>
                                    <option value="monthly">Once per month</option>
                                    <option value="other">Other / Irregular</option>
                                </select>
                            </label>
                            {/* Placeholder for affiliate links/catalog */}
                            <small>Common forms: D3 (Cholecalciferol) is generally preferred over D2 (Ergocalciferol).</small>
                        </div>
                    )}
                </div>

                {/* BMI */}
                <div className="form-section">
                    <h3>⚖️ Weight Status (Affects Needs)</h3>
                    <label>
                        Weight Class (Estimate):*
                        <select value={bmiCategory} onChange={(e) => setBmiCategory(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="normal">Normal Weight (BMI &lt; 25)</option>
                            <option value="overweight">Overweight (BMI 25-29.9)</option>
                            <option value="obese_low">Obese (BMI 30-34.9) - Need ~2x</option>
                            <option value="obese_high">Obese (BMI ≥ 35) - Need ~3x</option>
                        </select>
                        <small>Obesity increases needs (sequestration in fat); select multiplier range if obese.</small>
                    </label>
                </div>

                {/* Medications */}
                <div className="form-section">
                    <h3>💊 Medications Affecting Vitamin D</h3>
                    <fieldset>
                        <legend>Check any you take regularly (may decrease levels):</legend>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="glucocorticoids" checked={medications.glucocorticoids} onChange={handleMedicationChange} />
                            Glucocorticoids (e.g., Prednisone)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="anticonvulsants" checked={medications.anticonvulsants} onChange={handleMedicationChange} />
                            Anticonvulsants (some types, e.g., Phenytoin, Phenobarbital)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="orlistat" checked={medications.orlistat} onChange={handleMedicationChange} />
                            Orlistat (Weight loss drug)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="rifampin" checked={medications.rifampin} onChange={handleMedicationChange} />
                            Rifampin (Antibiotic)
                        </label>
                        <small>Long-term PPIs may indirectly affect Vit D via Magnesium.</small>
                    </fieldset>
                </div>

                {/* Health Flags */}
                <div className="form-section">
                    <h3>🩺 Health Conditions Affecting Vitamin D</h3>
                    <fieldset>
                        <legend>Check any that apply (may affect needs, metabolism, or safety):</legend>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="giDisease" checked={healthFlags.giDisease} onChange={handleHealthFlagChange} />
                            Malabsorption Condition (IBD, Celiac, Bariatric Surgery)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="liverDisease" checked={healthFlags.liverDisease} onChange={handleHealthFlagChange} />
                            Chronic Liver Disease
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="kidneyDisease" checked={healthFlags.kidneyDisease} onChange={handleHealthFlagChange} />
                            Chronic Kidney Disease (CKD)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="hyperparathyroidism" checked={healthFlags.hyperparathyroidism} onChange={handleHealthFlagChange} />
                            Hyperparathyroidism / High Blood Calcium History
                        </label>
                    </fieldset>
                </div>

                {/* Known Serum Level (Optional) */}
                <div className="form-section">
                    <h3>🩸 Known Vitamin D Level (Optional)</h3>
                    <label>
                        Most Recent 25-OH-D Level (ng/mL):
                        <input type="number" value={knownSerumLevelNgMl} onChange={(e) => setKnownSerumLevelNgMl(e.target.value)} min="0" step="any" placeholder="e.g., 25.5" />
                        <small>Enter if known. Helps contextualize results. (1 ng/mL ≈ 2.5 nmol/L)</small>
                    </label>
                </div>


                <button type="submit">Calculate Vitamin D Intake</button> {/* <<< UPDATED BUTTON TEXT */}
            </form>

            {/* --- Results Display --- */}
            {showResults && calculationResult && (
                <div className="results-section">
                    <h2>📊 Estimated Vitamin D Status</h2>
                    {/* Safety Flags Display */}
                    {calculationResult.safetyFlags.length > 0 && (
                        <div className="safety-alerts">
                            <h4>⚠️ Safety Alerts & Important Considerations</h4>
                            <ul>
                                {calculationResult.safetyFlags.map((flag, index) => (
                                    <li key={index} className={`alert-${flag.type}`}>
                                        <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Placeholder for Ring Gauge */}
                    <div className="result-gauge-placeholder">
                        <p>(Placeholder for Gauge: Effective IU vs Target)</p>
                        <div className="gauge-bar" style={{ '--effective-iu': calculationResult.effectiveTotalIntake, '--target-iu': calculationResult.targetRda }}>
                            <div className="gauge-fill"></div>
                        </div>
                        <span className="gauge-label">Effective: {calculationResult.effectiveTotalIntake} IU / Target: {calculationResult.targetRda} IU</span>
                    </div>

                    <div className="results-grid">
                        {/* Update result items for Vitamin D */}
                        <div className="result-item">
                            <span className="result-label">Personalized Target:</span>
                            <span className="result-value">{calculationResult.targetRda} IU/day</span>
                            <small className='result-note'>(Based on RDA/AI, adjusted for weight status)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Sun Synthesis:</span>
                            <span className="result-value">{calculationResult.sunIU} IU/day</span>
                            <small className='result-note'>(Highly variable, affected by many factors)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Dietary Intake:</span>
                            <span className="result-value">{calculationResult.dietIU} IU/day</span>
                            <small className='result-note'>(Avg. from weekly servings)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Avg. Supplement Intake:</span>
                            <span className="result-value">{calculationResult.suppIU} IU/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Absorption Factor:</span>
                            <span className="result-value">{calculationResult.absorptionFactor}x</span>
                            <small className='result-note'>(Applied to Sun + Diet due to meds/health)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Total Effective Intake:</span>
                            <span className="result-value significant">{calculationResult.effectiveTotalIntake} IU/day</span>
                            <small className='result-note'>(Sun*Factor + Diet*Factor + Supp)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Intake vs Target:</span>
                            <span className={`result-value status-${calculationResult.gap > calculationResult.targetRda * 0.5 ? 'low' : calculationResult.gap > 0 ? 'borderline' : 'adequate'}`}>
                                {calculationResult.gap > 0
                                    ? `Deficit (~${calculationResult.gap} IU)`
                                    : `Adequate / Surplus`
                                }
                            </span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Tolerable Upper Limit (UL):</span>
                            <span className="result-value">{calculationResult.ul} IU/day</span>
                            <small className='result-note'>(Max safe daily intake for age)</small>
                        </div>
                        {calculationResult.serumLevelProvided && (
                            <div className="result-item">
                                <span className="result-label">Reported 25-OH-D:</span>
                                <span className="result-value">{calculationResult.serumLevelProvided} ng/mL</span>
                                <small className='result-note'>(See interpretation in explanation/flags)</small>
                            </div>
                        )}
                    </div>

                    <h3>💡 Guidance & Suggestions</h3>
                    {/* Gap Statement */}
                    <p className="gap-statement">
                        {calculationResult.gap > 0
                            ? `You appear to be about ${calculationResult.gap} IU short of your personalized daily target.`
                            : `Your estimated intake meets or exceeds your personalized daily target.`
                        }
                    </p>

                    {/* Suggestions Sections */}
                    {calculationResult.suggestions.sun.length > 0 && (
                        <div className="suggestions-box">
                            <h4>☀️ Sun Exposure Tips:</h4>
                            <ul>{calculationResult.suggestions.sun.map((s, i) => <li key={`sun-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {calculationResult.suggestions.food.length > 0 && (
                        <div className="suggestions-box">
                            <h4>🍽️ Food Suggestions:</h4>
                            <ul>{calculationResult.suggestions.food.map((s, i) => <li key={`food-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {calculationResult.suggestions.supplement.length > 0 && (
                        <div className="suggestions-box">
                            <h4>💊 Supplement Considerations:</h4>
                            <ul>{calculationResult.suggestions.supplement.map((s, i) => <li key={`supp-${i}`}>{s}</li>)}</ul>
                            {/* Placeholder for affiliate links */}
                            {/* <p><small><i>Example supplement links could appear here.</i></small></p> */}
                        </div>
                    )}
                    {calculationResult.suggestions.test.length > 0 && (
                        <div className="suggestions-box">
                            <h4>🩸 Testing Prompt:</h4>
                            <ul>{calculationResult.suggestions.test.map((s, i) => <li key={`test-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}


                    <h3>📝 Calculation Explanation</h3>
                    <pre className="explanation-box">{calculationResult.explanation}</pre>

                    <p className="important-note">
                        <strong>Reminder:</strong> This is an educational tool, not medical advice. Individual responses to sun, diet, and supplements vary. Discuss these estimates and any planned changes with your healthcare provider, especially considering your health status and any known Vitamin D levels.
                    </p>
                    <a href="#vitd-education" className="scroll-link">Learn more about Vitamin D below</a> {/* <<< UPDATED LINK HASH */}
                </div>
            )}

            {/* --- Educational Content (Vitamin D specific) --- */}
            <div id="vitd-education" className="educational-section"> {/* <<< UPDATED ID */}
                <h2>Understanding Vitamin D</h2> {/* <<< UPDATED TITLE */}

                {/* Content Block 1: Biology & Metabolism */}
                <div className="content-block">
                    <h3>1. Vitamin D Essentials (Biology & Metabolism)</h3>
                    <dl>
                        <dt>Roles:</dt>
                        <dd>Crucial for calcium & phosphorus balance, bone mineralization, muscle function. Also involved in innate & adaptive immunity and mood regulation.</dd>

                        <dt>Forms:</dt>
                        <dd>
                            <ul>
                                <li><strong>D₂ (Ergocalciferol):</strong> From plants (esp. UV-exposed mushrooms) and fortified foods/supplements.</li>
                                <li><strong>D₃ (Cholecalciferol):</strong> Synthesized in skin upon UV-B exposure; also found in animal foods (fatty fish, egg yolks, liver) and supplements.</li>
                                <li><strong>Metabolism:</strong> Both D₂ and D₃ are converted in the liver to 25-hydroxyvitamin D [25(OH)D], the main circulating form and best indicator of status. The kidney then converts 25(OH)D to the active form, 1,25-dihydroxyvitamin D [1,25(OH)₂D].</li>
                            </ul>
                        </dd>

                        <dt>Cutaneous Synthesis:</dt>
                        <dd>Requires UV-B radiation (290–315 nm) acting on 7-dehydrocholesterol in the skin. Factors reducing synthesis include:
                            <ul>
                                <li>Higher latitudes (&gt; ≈37° N/S), especially in winter.</li>
                                <li>Older age (less 7-dehydrocholesterol in skin).</li>
                                <li>Darker skin pigmentation (melanin acts as natural sunscreen).</li>
                                <li>Sunscreen use (SPF ≥15 significantly blocks UV-B).</li>
                                <li>Extensive clothing coverage.</li>
                                <li>Air pollution and cloud cover.</li>
                            </ul>
                        </dd>

                        <dt>Serum Marker (25-OH-D):</dt>
                        <dd>Measured in ng/mL or nmol/L (1 ng/mL ≈ 2.5 nmol/L). Common interpretations:
                            <ul>
                                <li><strong>Deficient:</strong> &lt; 12 ng/mL (&lt; 30 nmol/L) - Risk of rickets/osteomalacia.</li>
                                <li><strong>Insufficient:</strong> 12–19 ng/mL (30–49 nmol/L) - Often suboptimal for bone health.</li>
                                <li><strong>Sufficient/Adequate (General Goal):</strong> ≥ 20 ng/mL (≥ 50 nmol/L) - Recommended by IOM.</li>
                                <li><strong>Optimal (Often Cited):</strong> ≥ 30 or 40 ng/mL (≥ 75 or 100 nmol/L) - Target suggested by some experts/societies for broader benefits, though evidence varies.</li>
                                <li><strong>Potential Risk:</strong> &gt; 50-60 ng/mL may offer no added benefit; &gt; 100 ng/mL raises concerns; &gt; 150 ng/mL indicates likely toxicity.</li>
                            </ul>
                        </dd>

                        <dt>UL (Tolerable Upper Intake Level):</dt>
                        <dd>Maximum daily intake unlikely to cause harm. For Vitamin D (from all sources):
                            <ul>
                                <li>0–6 months: 1,000 IU</li>
                                <li>7–12 months: 1,500 IU</li>
                                <li>1–3 years: 2,500 IU</li>
                                <li>4–8 years: 3,000 IU</li>
                                <li>9+ years (incl. pregnancy/lactation): 4,000 IU (100 µg)</li>
                            </ul>
                            Chronic intake above the UL increases risk of hypercalcemia (high blood calcium), hypercalciuria (high urine calcium), kidney stones, and soft tissue calcification.
                        </dd>

                        <dt>Drug Interactions & Obesity:</dt>
                        <dd>
                            <ul>
                                <li>Meds reducing levels: Glucocorticoids, certain anticonvulsants, orlistat, rifampin.</li>
                                <li>Obesity: Vitamin D is fat-soluble and stored (sequestered) in adipose tissue, reducing circulating levels. Individuals with obesity (BMI ≥ 30) may need 2–3 times the standard dose to achieve the same serum 25(OH)D increase.</li>
                                <li>PPIs: Long-term use may lower magnesium, which is needed for Vitamin D metabolism/action.</li>
                            </ul>
                        </dd>
                    </dl>
                </div>

                {/* Content Block 2: RDAs */}
                <div className="content-block">
                    <h3>2. Reference Intakes (RDA / AI) - Institute of Medicine (IOM)</h3>
                    <p>Assumes minimal sun exposure.</p>
                    <table>
                        <thead>
                            <tr><th>Life Stage</th><th>Target (IU/day)</th><th>µg equivalent*</th></tr>
                        </thead>
                        <tbody>
                            <tr><td>0–12 months †</td><td>400</td><td>10 µg</td></tr>
                            <tr><td>1–70 years</td><td>600</td><td>15 µg</td></tr>
                            <tr><td>71+ years</td><td>800</td><td>20 µg</td></tr>
                            <tr><td>Pregnant & Lactating</td><td>600</td><td>15 µg</td></tr>
                        </tbody>
                    </table>
                    <small>* 1 µg cholecalciferol = 40 IU Vitamin D. † AI (Adequate Intake) for infants.</small>
                </div>

                {/* Content Block 3: High Risk Groups */}
                <div className="content-block">
                    <h3>3. High-Risk / High-Need Groups</h3>
                    <ul>
                        <li><strong>Limited Sun Exposure:</strong> Institutionalized individuals (nursing homes), night-shift workers, those wearing full-cover clothing, people living at high latitudes (&gt;37° N/S) in winter.</li>
                        <li><strong>Darker Skin (Fitzpatrick IV-VI):</strong> Melanin reduces UV-B penetration, requiring longer sun exposure for similar Vitamin D synthesis.</li>
                        <li><strong>Older Adults (70+):</strong> Reduced skin synthesis capacity and potentially decreased kidney conversion to active form.</li>
                        <li><strong>Obesity (BMI ≥ 30):</strong> Sequestration in fat tissue lowers bioavailability; often need 2-3x higher intake.</li>
                        <li><strong>Malabsorption Syndromes:</strong> Conditions like Inflammatory Bowel Disease (IBD), Celiac Disease, or history of bariatric surgery impair fat-soluble vitamin absorption.</li>
                        <li><strong>Chronic Liver or Kidney Disease:</strong> Impaired ability to convert Vitamin D to its active forms (25-hydroxylation in liver, 1-hydroxylation in kidney).</li>
                        <li><strong>Certain Medications:</strong> Glucocorticoids, anticonvulsants, orlistat, rifampin can interfere with metabolism or absorption.</li>
                        <li><strong>Exclusively Breastfed Infants (without supplementation):</strong> Breast milk is typically low in Vitamin D; supplementation (400 IU/day) is recommended.</li>
                    </ul>
                </div>


                {/* --- Sources Section (Vitamin D specific) --- */}
                <div className="content-block">
                    <h3>Sources</h3>
                    <p className="sources"> {/* Ensure .sources class has appropriate styling */}
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Vitamin D Fact Sheet for Health Professionals*. Retrieved [April-14-2025], from https://ods.od.nih.gov/factsheets/VitaminD-HealthProfessional/ <br />
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Vitamin D Fact Sheet for Consumers*. Retrieved [April-14-2025], from https://ods.od.nih.gov/factsheets/VitaminD-Consumer/ <br />
                        Institute of Medicine (US) Committee to Review Dietary Reference Intakes for Vitamin D and Calcium. (2011). *Dietary Reference Intakes for Calcium and Vitamin D*. The National Academies Press. https://doi.org/10.17226/13050 <br />
                        Holick, M. F. (2007). Vitamin D deficiency. *New England Journal of Medicine*, *357*(3), 266-281. (General overview & synthesis info) <br />
                        Webb, A. R., Kline, L., & Holick, M. F. (1988). Influence of season and latitude on the cutaneous synthesis of vitamin D3: exposure to winter sunlight in Boston and Edmonton will not promote vitamin D3 synthesis in human skin. *The Journal of Clinical Endocrinology & Metabolism*, *67*(2), 373-378. (Latitude/Season effects) <br />
                        Clemens, T. L., Adams, J. S., Henderson, S. L., & Holick, M. F. (1982). Increased skin pigment reduces the capacity of skin to synthesise vitamin D3. *The Lancet*, *319*(8263), 74-76. (Skin pigment effects) <br />
                        Wacker, M., & Holick, M. F. (2013). Sunlight and Vitamin D: A global perspective for health. *Dermato-endocrinology*, *5*(1), 51–108. (Obesity, general factors) <br />
                        Selected citations from PMC as indicated in the prompt data for specific points like drug interactions, obesity effect, etc. (Full citations would be needed here).<br />
                        USDA FoodData Central. (n.d.). Retrieved [April-14-2025], from https://fdc.nal.usda.gov/ (For food values)
                    </p>
                    {/* Remember to add Retrieval Dates */}
                </div>
                {/* --- END OF SOURCES SECTION --- */}

            </div> {/* End educational-section */}
        </div> // End vitaminDCalculator
    );
}

export default VitaminDCalculator; 